import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classes from './CalendarControls.module.scss';

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function formatCount(n) {
  if (n == null) return '';
  if (n >= 1000) return new Intl.NumberFormat('en-US').format(n);
  return String(n);
}

function Pill({ label, count, active, disabled, onClick, title }) {
  return (
    <button
      type="button"
      className={`${classes.pill} ${active ? classes.active : ''}`}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      <span className={classes.pillLabel}>{label}</span>
      <span className={classes.pillCount}>{formatCount(count)}</span>
    </button>
  );
}

Pill.propTypes = {
  label: PropTypes.string.isRequired,
  count: PropTypes.number,
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  title: PropTypes.string,
};

Pill.defaultProps = {
  count: 0,
  active: false,
  disabled: false,
  title: undefined,
};

export default function CalendarControls({
  year,
  month,
  view,
  yearOptions,
  yearTotals,
  monthTotals,
  onYearChange,
  onMonthChange,
}) {
  const { t } = useTranslation('dashboard\\SyncMonitorView', {
    useSuspense: false,
  });

  return (
    <div className={classes.wrap}>
      <div className={classes.row}>
        <span className={classes.label}>{t('yearLabel')}</span>
        <div className={classes.pillStrip}>
          {yearOptions.map((y) => (
            <Pill
              key={y}
              label={String(y)}
              count={yearTotals?.[String(y)] ?? 0}
              active={year === y}
              onClick={() => onYearChange(y)}
            />
          ))}
        </div>
      </div>

      <div className={classes.row}>
        <span className={classes.label}>{t('monthLabel')}</span>
        <div className={classes.pillStrip}>
          {MONTH_LABELS.map((name, i) => {
            const m = i + 1;
            const isYearView = view === 'year';
            return (
              <Pill
                key={m}
                label={name}
                count={monthTotals?.[m] ?? 0}
                active={!isYearView && month === m}
                disabled={isYearView}
                onClick={() => onMonthChange(m)}
                title={isYearView ? t('monthDisabledHint') : undefined}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

CalendarControls.propTypes = {
  year: PropTypes.number.isRequired,
  month: PropTypes.number.isRequired,
  view: PropTypes.oneOf(['year', 'month']).isRequired,
  yearOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
  yearTotals: PropTypes.object,
  monthTotals: PropTypes.object,
  onYearChange: PropTypes.func.isRequired,
  onMonthChange: PropTypes.func.isRequired,
};

CalendarControls.defaultProps = {
  yearTotals: null,
  monthTotals: null,
};
